import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import PortalAlert from "./PortalAlert";
import PortalFormPage from "./PortalFormPage";
import PortalResultsHead from "./PortalResultsHead";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";
import { formatBusinessName } from "../utils/formatters";

const PORTAL_PROJECTS_ROWS_PER_PAGE_KEY = "portalProjectsRowsPerPage";
const PORTAL_PROJECTS_CURRENT_PAGE_KEY = "portalProjectsCurrentPage";

const PROJECT_STATUSES = ["Active", "Inactive"];

const EMPTY_FORM = {
  clientId: "",
  projectName: "",
  shortCode: "",
  description: "",
  address: "",
  totalBeds: "",
  availableBeds: "",
  rentRange: "",
  projectUrl: "",
  amenities: [],
  amenityDraft: "",
  status: "Active",
};

const COLUMNS = [
  { key: "projectName", label: "Project Name" },
  { key: "shortCode", label: "Short Code" },
  { key: "clientName", label: "Client Name" },
  { key: "address", label: "Address" },
  { key: "beds", label: "Beds" },
  { key: "rentRange", label: "Rent Range" },
  { key: "status", label: "Status" },
];

const SORT_ACCESSORS = {
  projectName: (p) => (p.Project_Name || "").toLowerCase(),
  shortCode: (p) => (p.Short_Code || "").toLowerCase(),
  clientName: (p) => (p.Client_Name || "").toLowerCase(),
  address: (p) => (p.Address || "").toLowerCase(),
  beds: (p) => Number(p.Total_Beds) || 0,
  rentRange: (p) => (p.Rent_Range || "").toLowerCase(),
  status: (p) => (p.Status || "").toLowerCase(),
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function bedsDisplay(project) {
  const total = project?.Total_Beds;
  const available = project?.Available_Beds;
  if (total == null && available == null) return "—";
  if (total != null && available != null) return `${available} / ${total}`;
  if (available != null) return String(available);
  return String(total);
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function buildProjectPayload(form) {
  const parseBeds = (value) => {
    if (value === "" || value == null) return null;
    const n = Number.parseInt(String(value).trim(), 10);
    return Number.isNaN(n) ? null : n;
  };
  return {
    Project_Name: form.projectName.trim(),
    Short_Code: form.shortCode.trim(),
    Description: form.description.trim() || null,
    Address: form.address.trim() || null,
    Total_Beds: parseBeds(form.totalBeds),
    Available_Beds: parseBeds(form.availableBeds),
    Rent_Range: form.rentRange.trim() || null,
    Project_URL: form.projectUrl.trim() || null,
    Amenities: Array.isArray(form.amenities)
      ? form.amenities.map((item) => String(item).trim()).filter(Boolean)
      : [],
    Status: form.status || "Active",
  };
}

function TenantProjects({ createMode = false }) {
  const navigate = useNavigate();
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [sortKey, setSortKey] = useState("projectName");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_PROJECTS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_PROJECTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_PROJECTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadProjects = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-projects");
    setProjects(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  const loadClients = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-clients");
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
    setClients(
      rows.filter((c) => {
        if (String(c.Status || "").toLowerCase() === "inactive") return false;
        const sub = String(c.Business_Sub_Type || "").trim();
        return !sub || sub === "Project" || sub === "Both";
      })
    );
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await Promise.all([loadProjects(), loadClients()]);
      } catch (_) {
        if (active) setError("Could not load projects.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadProjects, loadClients, createMode]);

  useEffect(() => {
    localStorage.setItem(PORTAL_PROJECTS_CURRENT_PAGE_KEY, String(currentPage));
  }, [currentPage]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter && String(p.Status || "") !== statusFilter) return false;
      if (clientFilter && String(p.Tenant_Client_Id) !== String(clientFilter)) return false;
      if (!q) return true;
      const haystack = [
        p.Project_Name,
        p.Short_Code,
        p.Client_Name,
        p.Address,
        p.Rent_Range,
        p.Description,
        Array.isArray(p.Amenities) ? p.Amenities.join(" ") : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [projects, search, statusFilter, clientFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((p) =>
      [
        p.Project_Name,
        p.Short_Code,
        p.Client_Name,
        p.Address,
        p.Rent_Range,
        p.Status,
        p.Description,
        Array.isArray(p.Amenities) ? p.Amenities.join(" ") : "",
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.projectName;
    const next = [...resultFiltered];
    next.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return next;
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage) || 1);
  const page = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const skipFilterPageResetRef = useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, statusFilter, clientFilter]);

  const hasActiveFilters = Boolean(search || statusFilter || clientFilter);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setClientFilter("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!createMode) return;
    setForm(EMPTY_FORM);
    setFormError("");
  }, [createMode]);

  const closeAddForm = () => navigate("/portal/projects");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientId) {
      setFormError("Client name is required.");
      return;
    }
    if (!form.projectName.trim()) {
      setFormError("Project name is required.");
      return;
    }
    if (!form.shortCode.trim()) {
      setFormError("Short code is required.");
      return;
    }
    const totalBeds = form.totalBeds === "" ? null : Number.parseInt(form.totalBeds, 10);
    const availableBeds =
      form.availableBeds === "" ? null : Number.parseInt(form.availableBeds, 10);
    if (form.totalBeds !== "" && Number.isNaN(totalBeds)) {
      setFormError("Total beds must be a number.");
      return;
    }
    if (form.availableBeds !== "" && Number.isNaN(availableBeds)) {
      setFormError("Available beds must be a number.");
      return;
    }
    if (totalBeds != null && availableBeds != null && availableBeds > totalBeds) {
      setFormError("Available beds cannot be greater than total beds.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post(
        `/client/tenant-clients/${form.clientId}/projects`,
        buildProjectPayload(form)
      );
      closeAddForm();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create project."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page cportal-page--projects">
      {!createMode && (
        <>
      <div className="cportal-page-head">
        <h1>Projects</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search project, short code, client, address"
        searchAriaLabel="Search projects"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/projects/new")}
          >
            Add Project
          </button>
        }
      >
        <div className="cportal-filter-row">
          <select
            value={clientFilter}
            onChange={(e) => {
              setClientFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                {c.Client_Name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Projects"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
          ariaLabel="Keyword search projects"
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {projects.length === 0 ? "No projects added yet." : "No projects match your filters."}
          </p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((project) => (
                  <tr key={project.Tenant_Project_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/clients/${project.Tenant_Client_Id}`)}
                      >
                        {display(project.Project_Name)}
                      </button>
                    </td>
                    <td>{display(project.Short_Code)}</td>
                    <td>{display(formatBusinessName(project.Client_Name))}</td>
                    <td>{display(project.Address)}</td>
                    <td>{bedsDisplay(project)}</td>
                    <td>{display(project.Rent_Range)}</td>
                    <td>
                      <span className={statusBadgeClass(project.Status)}>
                        {display(project.Status)}
                      </span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() =>
                            navigate(`/portal/clients/${project.Tenant_Client_Id}`)
                          }
                          title="View client"
                          aria-label="View client"
                        >
                          <FaEye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel={
              projects.length === 0
                ? "No projects added yet."
                : "No projects match your filters."
            }
            className="cportal-projects-mobile"
          >
            {pageRows.map((project) => {
              const status = project.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              return (
                <ResultsMobileCard
                  key={project.Tenant_Project_Id}
                  title={display(project.Project_Name)}
                  subtitle={project.Short_Code || formatBusinessName(project.Client_Name) || null}
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    project.Client_Name
                      ? { label: formatBusinessName(project.Client_Name), tone: "info" }
                      : null,
                  ].filter(Boolean)}
                  fields={[
                    { label: "Address", value: project.Address },
                    {
                      label: "Beds",
                      value: bedsDisplay(project) !== "—" ? bedsDisplay(project) : null,
                    },
                    { label: "Rent", value: project.Rent_Range },
                  ]}
                  onOpen={() =>
                    navigate(`/portal/clients/${project.Tenant_Client_Id}`)
                  }
                  actions={
                    <div className="co-mcard__action-grid">
                      <button
                        type="button"
                        className="co-maction"
                        title="View client"
                        aria-label="View client"
                        onClick={() =>
                          navigate(`/portal/clients/${project.Tenant_Client_Id}`)
                        }
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {!loading && sorted.length > 0 && (
          <TablePagination
            currentPage={page}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="projects"
          />
        )}
      </div>

        </>
      )}

      {createMode && (
        <PortalFormPage
          title="Add Project"
          backLabel="Back to projects"
          onBack={closeAddForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Save"
          submittingLabel="Saving…"
        >
              <PortalAlert
                type="error"
                message={formError}
                onClear={() => setFormError("")}
              />
              <div className="cportal-form-grid">
                <div className="cportal-field cportal-field--full">
                  <label>
                    Client Name <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.clientId}
                    onChange={(e) => setField("clientId", e.target.value)}
                    required
                  >
                    <option value="">Select Client</option>
                    {clients.map((c) => (
                      <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                        {c.Client_Name}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>
                    Project Name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.projectName}
                    onChange={(e) => setField("projectName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Short Code <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.shortCode}
                    onChange={(e) => setField("shortCode", e.target.value)}
                    maxLength={20}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Address</label>
                  <textarea
                    rows={2}
                    value={form.address}
                    onChange={(e) => setField("address", e.target.value)}
                    placeholder="Enter project address"
                    maxLength={500}
                  />
                </div>
                <div className="cportal-field">
                  <label>Total Beds</label>
                  <input
                    type="number"
                    min="0"
                    value={form.totalBeds}
                    onChange={(e) => setField("totalBeds", e.target.value)}
                    placeholder="e.g. 100"
                  />
                </div>
                <div className="cportal-field">
                  <label>Available Beds</label>
                  <input
                    type="number"
                    min="0"
                    value={form.availableBeds}
                    onChange={(e) => setField("availableBeds", e.target.value)}
                    placeholder="e.g. 25"
                  />
                </div>
                <div className="cportal-field">
                  <label>Rent Range</label>
                  <input
                    type="text"
                    value={form.rentRange}
                    onChange={(e) => setField("rentRange", e.target.value)}
                    placeholder="e.g. 8000 - 15000"
                    maxLength={100}
                  />
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Project page link</label>
                  <input
                    type="url"
                    value={form.projectUrl}
                    onChange={(e) => setField("projectUrl", e.target.value)}
                    placeholder="https://your-site.com/project-details"
                    maxLength={1000}
                  />
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Amenities</label>
                  <div className="cportal-amenity-editor">
                    <div className="cportal-amenity-input-row">
                      <input
                        value={form.amenityDraft}
                        onChange={(e) => setField("amenityDraft", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          const next = String(form.amenityDraft || "").trim();
                          if (!next) return;
                          const existing = Array.isArray(form.amenities) ? form.amenities : [];
                          if (existing.some((item) => item.toLowerCase() === next.toLowerCase())) {
                            setField("amenityDraft", "");
                            return;
                          }
                          setForm((f) => ({
                            ...f,
                            amenities: [...(Array.isArray(f.amenities) ? f.amenities : []), next],
                            amenityDraft: "",
                          }));
                        }}
                        placeholder="Type amenity and press Add"
                      />
                      <button
                        type="button"
                        className="cportal-btn cportal-btn-edit"
                        onClick={() => {
                          const next = String(form.amenityDraft || "").trim();
                          if (!next) return;
                          const existing = Array.isArray(form.amenities) ? form.amenities : [];
                          if (existing.some((item) => item.toLowerCase() === next.toLowerCase())) {
                            setField("amenityDraft", "");
                            return;
                          }
                          setForm((f) => ({
                            ...f,
                            amenities: [...(Array.isArray(f.amenities) ? f.amenities : []), next],
                            amenityDraft: "",
                          }));
                        }}
                      >
                        Add
                      </button>
                    </div>
                    {Array.isArray(form.amenities) && form.amenities.length > 0 && (
                      <div className="cportal-amenity-chips">
                        {form.amenities.map((item, index) => (
                          <span key={`${item}-${index}`} className="cportal-amenity-chip">
                            {item}
                            <button
                              type="button"
                              aria-label={`Remove ${item}`}
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  amenities: (Array.isArray(f.amenities) ? f.amenities : []).filter(
                                    (_, i) => i !== index
                                  ),
                                }))
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    maxLength={500}
                  />
                </div>
              </div>
        </PortalFormPage>
      )}
    </div>
  );
}

export default TenantProjects;
